const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:7072/api';

export interface MvUpdatedEvent {
  type: 'mv_updated';
  lastEventId: string;
  eventType: string;
}

export class NotificationService {
  private ws: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectDelay = 1000;
  private maxReconnectDelay = 30000;
  private sessionId: string | null = null;
  private onUpdate: ((event: MvUpdatedEvent) => void) | null = null;
  private apiBaseUrl: string;
  private disposed = false;

  constructor(apiBaseUrl?: string) {
    this.apiBaseUrl = apiBaseUrl || API_BASE_URL;
  }

  async connect(
    sessionId: string,
    onUpdate: (event: MvUpdatedEvent) => void
  ): Promise<void> {
    this.sessionId = sessionId;
    this.onUpdate = onUpdate;
    this.disposed = false;
    this.reconnectDelay = 1000;

    await this.doConnect();
  }

  private async doConnect(): Promise<void> {
    if (this.disposed || !this.sessionId) return;

    try {
      // Negotiate to get WebSocket URL
      const headers: Record<string, string> = {};
      const functionsKey = import.meta.env.VITE_FUNCTIONS_KEY;
      if (functionsKey) {
        headers['x-functions-key'] = functionsKey;
      }
      const response = await fetch(
        `${this.apiBaseUrl}/sessions/${this.sessionId}/negotiate`,
        { headers }
      );

      if (!response.ok) {
        throw new Error(`Negotiate failed: ${response.status}`);
      }

      const { url } = await response.json();

      // For Docker dev: the negotiate endpoint returns ws://localhost:8088/...
      // but we may need to override via env var
      const wsUrl = import.meta.env.VITE_WS_BASE_URL
        ? `${import.meta.env.VITE_WS_BASE_URL}/ws?sessionId=${this.sessionId}`
        : url;

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log(`[WS-NOTIFY] Connected for session ${this.sessionId}`);
        this.reconnectDelay = 1000; // Reset on successful connect
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as MvUpdatedEvent;
          if (data.type === 'mv_updated' && this.onUpdate) {
            this.onUpdate(data);
          }
        } catch (err) {
          console.warn('[WS-NOTIFY] Failed to parse message:', event.data);
        }
      };

      this.ws.onclose = (event) => {
        console.log(
          `[WS-NOTIFY] Disconnected (code: ${event.code}). Reconnecting in ${this.reconnectDelay}ms...`
        );
        this.scheduleReconnect();
      };

      this.ws.onerror = (event) => {
        console.warn('[WS-NOTIFY] WebSocket error:', event);
        // onclose will fire after onerror, which will trigger reconnect
      };
    } catch (err) {
      console.warn('[WS-NOTIFY] Connection failed:', err);
      this.scheduleReconnect();
    }
  }

  private scheduleReconnect(): void {
    if (this.disposed) return;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectDelay = Math.min(
        this.reconnectDelay * 2,
        this.maxReconnectDelay
      );
      this.doConnect();
    }, this.reconnectDelay);
  }

  disconnect(): void {
    this.disposed = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.onclose = null; // Prevent reconnect on intentional close
      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close(1000, 'Client disconnecting');
      }
      this.ws = null;
    }

    this.sessionId = null;
    this.onUpdate = null;
  }
}
